import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { AppHeader } from "@/components/Layout/AppHeader";
import { BadgeGrid } from "@/components/Gamification/BadgeGrid";
import { retroactivelyAwardBadges, checkAndAwardBadgesForEvent } from "@/lib/badges";
import { getUserProgress } from "@/lib/gamification";
import { useToast } from "@/hooks/use-toast";
import { getVersionString } from "@/lib/version";
import { useLanguage } from "@/contexts/LanguageContext";
import { populateMonthsUnderBudget } from "@/lib/budgetFinisher";

const Badges = () => {
  const { t } = useLanguage();
  const [user, setUser] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate('/auth');
      } else {
        // Populate months_under_budget before checking badges
        populateMonthsUnderBudget(session.user.id).catch(console.error);
        // Retroactively award badges + check time-based badges on page open
        retroactivelyAwardBadges(session.user.id).catch(console.error);
        getUserProgress(session.user.id).then(progress => {
          checkAndAwardBadgesForEvent(session.user.id, 'badges_page_opened', progress).catch(console.error);
        });
      }
    });
  }, [navigate]);

  const handleRefresh = async () => {
    if (!user) return;
    
    setRefreshing(true);
    try {
      const newBadges = await retroactivelyAwardBadges(user.id);
      if (newBadges.length > 0) {
        toast({
          title: t('badges.newBadgesEarned'),
          description: t('badges.earnedCount', {
            count: newBadges.length,
            s: newBadges.length > 1 ? 's' : ''
          }),
        });
      } else {
        toast({
          title: t('badges.allCaughtUp'),
          description: t('badges.noNewBadges'),
        });
      }
      setRefreshKey(prev => prev + 1); // Force BadgeGrid to refresh
    } catch (error) {
      console.error('Error refreshing badges:', error);
      toast({
        title: t('badges.couldntRefresh'),
        description: t('badges.tryAgain'),
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">{t('badges.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        showDrawerTrigger={true}
        title={t('badges.title')}
        rightActions={
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="min-h-[44px] min-w-[44px]"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        }
      />

      <main className="container mx-auto px-6 pt-20 pb-6 max-w-4xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">{t('badges.achievements')}</h2>
          <p className="text-muted-foreground">
            {t('badges.hideAnytime')}
          </p>
        </div>

        <BadgeGrid key={refreshKey} userId={user.id} />

        <footer className="text-xs text-muted-foreground text-center py-4 mt-6 border-t">
          {getVersionString()}
        </footer>
      </main>
    </div>
  );
};

export default Badges;
